import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { fmtTime, statusStyles } from '../../lib/format';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8..19

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function AdminCalendarPage() {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [appts, setAppts] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [advisorId, setAdvisorId] = useState('');
  const [view, setView] = useState('week'); // week, day

  const days = useMemo(() => Array.from({ length: view === 'week' ? 7 : 1 }, (_, i) => {
    const d = new Date(anchor); d.setDate(anchor.getDate() + i); return d;
  }), [anchor, view]);

  useEffect(() => { api.adminAdvisors().then(setAdvisors); }, []);

  useEffect(() => {
    const start = new Date(days[0]); start.setHours(0,0,0,0);
    const end = new Date(days[days.length-1]); end.setHours(23,59,59,999);
    api.calendar({ start: start.toISOString(), end: end.toISOString(), advisor_id: advisorId || undefined }).then(setAppts);
  }, [days, advisorId]);

  const move = (delta) => {
    const d = new Date(anchor); d.setDate(anchor.getDate() + delta * (view === 'week' ? 7 : 1));
    setAnchor(view === 'week' ? startOfWeek(d) : d);
  };

  return (
    <AdminLayout title="Calendar" subtitle={`${days[0].toLocaleDateString('en-CA', { month: 'long', day: 'numeric' })} – ${days[days.length-1].toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}`}
      actions={
        <div className="flex items-center gap-2">
          <select value={advisorId} onChange={(e) => setAdvisorId(e.target.value)} className="px-3 py-2 rounded-md border border-neutral-200 text-[13px] focus:border-[#f5b300] outline-none">
            <option value="">All advisors</option>
            {advisors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div className="flex rounded-md border border-neutral-200 overflow-hidden">
            {['day','week'].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-2 text-[12.5px] font-semibold uppercase ${view===v?'bg-[#f5b300] text-[#14110f]':'text-neutral-600 hover:bg-neutral-50'}`}>{v}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => move(-1)} className="w-9 h-9 rounded-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setAnchor(startOfWeek(new Date()))} className="px-3 h-9 rounded-md border border-neutral-200 text-[12.5px] font-semibold hover:bg-neutral-50">Today</button>
            <button onClick={() => move(1)} className="w-9 h-9 rounded-md border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      }
    >
      <div className="rounded-xl bg-white border border-neutral-100 overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
          <div className="bg-[#faf6ec] border-b border-neutral-100" />
          {days.map((d) => {
            const isToday = new Date().toDateString() === d.toDateString();
            return (
              <div key={d.toISOString()} className="border-b border-l border-neutral-100 bg-[#faf6ec] p-3 text-center">
                <p className="text-[10.5px] uppercase tracking-wider text-neutral-500">{d.toLocaleDateString('en-CA', { weekday: 'short' })}</p>
                <p className={`mt-1 text-[18px] font-extrabold ${isToday ? 'text-[#c89200]' : 'text-[#14110f]'}`}>{d.getDate()}</p>
              </div>
            );
          })}
          {HOURS.map((h) => (
            <React.Fragment key={h}>
              <div className="px-2 py-2 text-right text-[10.5px] text-neutral-400 border-r border-neutral-100">{h%12||12}{h<12?'am':'pm'}</div>
              {days.map((d) => {
                const cellStart = new Date(d); cellStart.setHours(h, 0, 0, 0);
                const cellEnd = new Date(d); cellEnd.setHours(h + 1, 0, 0, 0);
                const cellAppts = appts.filter((a) => {
                  const s = new Date(a.start);
                  return s >= cellStart && s < cellEnd;
                });
                return (
                  <div key={d.toISOString() + h} className="relative border-l border-t border-neutral-100 min-h-[72px] p-1.5">
                    {cellAppts.map((a) => {
                      const ss = statusStyles[a.status] || statusStyles.confirmed;
                      return (
                        <Link key={a.id} to={`/admin/appointments/${a.id}`} className={`block px-2 py-1.5 rounded-md mb-1 text-[11.5px] font-semibold ${ss.bg} ${ss.text} border-l-2 border-[#f5b300] hover:shadow-md transition`}>
                          <p className="truncate">{fmtTime(a.start)} · {a.appointment_type_name}</p>
                          <p className="text-[10.5px] font-normal opacity-80 truncate">{a.client_first_name} {a.client_last_name}</p>
                        </Link>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
